import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Heart,
  Check,
  Cigarette,
  Moon,
  Barbell,
  Fire,
  Drop,
  Wine,
  UserCircle,
  Warning,
  Info
} from '@phosphor-icons/react';

// Age range for predictions (must match backend)
const AGE_RANGE = { min: 35, max: 58 };

// Validate age - strict validation
const validateAge = (age) => {
  if (!age) return null; // Age is optional but validated if provided
  const numAge = parseInt(age);
  if (isNaN(numAge)) return 'Please enter a valid age';
  if (numAge < AGE_RANGE.min) return `Our predictions are designed for women aged ${AGE_RANGE.min} and above. If you're younger, please check back in a few years.`;
  if (numAge > AGE_RANGE.max) return `Our predictions are optimized for women up to age ${AGE_RANGE.max}. You may have already completed your menopause journey.`;
  return null;
};

export default function Lifestyle() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [ageError, setAgeError] = useState(null);
  
  const [lifestyleData, setLifestyleData] = useState({
    age: '',
    bmi: '',
    smoking_status: '',
    alcohol_consumption: '',
    sleep_hours: 7,
    sleep_quality: 5,
    exercise_frequency: '',
    exercise_type: '',
    ethnicity: ''
  });

  const [symptomsData, setSymptomsData] = useState({
    hot_flashes_frequency: 0,
    hot_flashes_severity: '',
    sleep_issues: false,
    cycle_regularity: '',
    cycle_length_change: '',
    mood_changes: false,
    vaginal_dryness: false
  });

  const handleLifestyleChange = (name, value) => {
    setLifestyleData(prev => ({ ...prev, [name]: value }));
    if (name === 'age') {
      setAgeError(null); // Clear error when user types
    }
  };

  const handleSymptomsChange = (name, value) => {
    setSymptomsData(prev => ({ ...prev, [name]: value }));
  };

  const handleNextStep = () => {
    // Validate age before proceeding
    if (lifestyleData.age) {
      const ageValidationError = validateAge(lifestyleData.age);
      if (ageValidationError) {
        setAgeError(ageValidationError);
        toast.error('Please fix the age validation error');
        return;
      }
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    // Final validation check
    if (lifestyleData.age) {
      const ageValidationError = validateAge(lifestyleData.age);
      if (ageValidationError) {
        setAgeError(ageValidationError);
        toast.error('Please fix the age validation error');
        setStep(1); // Go back to step 1 to fix the error
        return;
      }
    }
    
    setLoading(true);

    try {
      // Save lifestyle data
      await api.createLifestyleData({
        ...lifestyleData,
        age: lifestyleData.age ? parseInt(lifestyleData.age) : null,
        bmi: lifestyleData.bmi ? parseFloat(lifestyleData.bmi) : null,
        sleep_hours: lifestyleData.sleep_hours,
        sleep_quality: lifestyleData.sleep_quality
      });

      // Save symptoms data
      await api.createSymptomsData(symptomsData);

      toast.success('Lifestyle and symptoms data saved!');
      navigate('/dashboard', { replace: true, state: { refresh: Date.now(), dataUpdated: true } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save data');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8F6FF] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#5B21B6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8F6FF]">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#5B21B6] hover:text-[#4C1D95]">
            <ArrowLeft className="w-5 h-5" weight="bold" />
            Back to Dashboard
          </Link>
          <Link to="/" className="text-xl font-bold text-[#5B21B6] font-['Outfit']">
            MenoPath
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#34D399] to-[#10B981] flex items-center justify-center">
              <Heart className="w-8 h-8 text-white" weight="duotone" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#1E1B4B] font-['Outfit']">
                Lifestyle Assessment
              </h1>
              <p className="text-[#6B7280]">Help us understand your health profile</p>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="flex gap-2 mb-8">
            <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-[#5B21B6]' : 'bg-gray-200'}`} />
            <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-[#5B21B6]' : 'bg-gray-200'}`} />
          </div>

          {/* Step 1: Lifestyle */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              <div className="glass-card rounded-3xl p-6">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6">
                  Basic Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-[#1E1B4B] font-medium">
                      Age
                      <span className="text-sm font-normal text-[#6B7280] ml-2">({AGE_RANGE.min}-{AGE_RANGE.max} years)</span>
                    </Label>
                    <Input
                      id="age"
                      type="number"
                      value={lifestyleData.age}
                      onChange={(e) => handleLifestyleChange('age', e.target.value)}
                      placeholder={`Enter your age (${AGE_RANGE.min}-${AGE_RANGE.max})`}
                      className={`h-12 rounded-xl border-2 ${ageError ? 'border-red-400 focus:border-red-500' : ''}`}
                      data-testid="age-input"
                    />
                    {ageError && (
                      <motion.div 
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-start gap-2 mt-2"
                      >
                        <Warning className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" weight="fill" />
                        <p className="text-sm text-red-600">{ageError}</p>
                      </motion.div>
                    )}
                    {!ageError && lifestyleData.age && (
                      <motion.div 
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 mt-2"
                      >
                        <Check className="w-4 h-4 text-green-500" weight="bold" />
                        <p className="text-sm text-green-600">Age is within valid range</p>
                      </motion.div>
                    )}
                    {!lifestyleData.age && (
                      <p className="text-xs text-[#6B7280] flex items-start gap-1 mt-1">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" weight="fill" />
                        Our predictions are optimized for women aged {AGE_RANGE.min}-{AGE_RANGE.max}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bmi" className="text-[#1E1B4B] font-medium">BMI</Label>
                    <Input
                      id="bmi"
                      type="number"
                      step="0.1"
                      value={lifestyleData.bmi}
                      onChange={(e) => handleLifestyleChange('bmi', e.target.value)}
                      placeholder="Enter your BMI"
                      className="h-12 rounded-xl border-2"
                      data-testid="bmi-input"
                    />
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <UserCircle className="w-6 h-6 text-[#8B5CF6]" weight="duotone" />
                  <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
                    Ethnicity
                  </h2>
                </div>
                <p className="text-sm text-[#6B7280] mb-4">
                  Ethnicity can influence the timing of menopause. Research shows variations across different ethnic groups.
                </p>
                <Select 
                  value={lifestyleData.ethnicity} 
                  onValueChange={(value) => handleLifestyleChange('ethnicity', value)}
                >
                  <SelectTrigger className="h-12 rounded-xl border-2" data-testid="ethnicity-select">
                    <SelectValue placeholder="Select your ethnicity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="caucasian">Caucasian / White</SelectItem>
                    <SelectItem value="african_american">African American / Black</SelectItem>
                    <SelectItem value="hispanic">Hispanic / Latina</SelectItem>
                    <SelectItem value="asian">Asian</SelectItem>
                    <SelectItem value="native_american">Native American / Indigenous</SelectItem>
                    <SelectItem value="middle_eastern">Middle Eastern</SelectItem>
                    <SelectItem value="pacific_islander">Pacific Islander</SelectItem>
                    <SelectItem value="mixed">Mixed / Multiracial</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="glass-card rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Cigarette className="w-6 h-6 text-[#FB923C]" weight="duotone" />
                  <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
                    Smoking Status
                  </h2>
                </div>
                <Select 
                  value={lifestyleData.smoking_status} 
                  onValueChange={(value) => handleLifestyleChange('smoking_status', value)}
                >
                  <SelectTrigger className="h-12 rounded-xl border-2" data-testid="smoking-select">
                    <SelectValue placeholder="Select smoking status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never smoked</SelectItem>
                    <SelectItem value="former">Former smoker</SelectItem>
                    <SelectItem value="current">Current smoker</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="glass-card rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Wine className="w-6 h-6 text-[#7C3AED]" weight="duotone" />
                  <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
                    Alcohol Consumption
                  </h2>
                </div>
                <Select 
                  value={lifestyleData.alcohol_consumption} 
                  onValueChange={(value) => handleLifestyleChange('alcohol_consumption', value)}
                >
                  <SelectTrigger className="h-12 rounded-xl border-2" data-testid="alcohol-select">
                    <SelectValue placeholder="Select alcohol consumption" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never drink</SelectItem>
                    <SelectItem value="rarely">Rarely (special occasions)</SelectItem>
                    <SelectItem value="moderate">Moderate (1-7 drinks/week)</SelectItem>
                    <SelectItem value="heavy">Heavy (8+ drinks/week)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="glass-card rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Moon className="w-6 h-6 text-[#5B21B6]" weight="duotone" />
                  <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
                    Sleep Habits
                  </h2>
                </div>
                <div className="space-y-6">
                  <div>
                    <Label className="text-[#1E1B4B] font-medium mb-4 block">
                      Average sleep hours per night: {lifestyleData.sleep_hours}
                    </Label>
                    <Slider
                      value={[lifestyleData.sleep_hours]}
                      onValueChange={(value) => handleLifestyleChange('sleep_hours', value[0])}
                      min={4}
                      max={12}
                      step={0.5}
                      className="w-full"
                      data-testid="sleep-hours-slider"
                    />
                    <div className="flex justify-between text-sm text-[#6B7280] mt-2">
                      <span>4 hrs</span>
                      <span>12 hrs</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#1E1B4B] font-medium mb-4 block">
                      Sleep quality: {lifestyleData.sleep_quality}/10
                    </Label>
                    <Slider
                      value={[lifestyleData.sleep_quality]}
                      onValueChange={(value) => handleLifestyleChange('sleep_quality', value[0])}
                      min={1}
                      max={10}
                      step={1}
                      className="w-full"
                      data-testid="sleep-quality-slider"
                    />
                    <div className="flex justify-between text-sm text-[#6B7280] mt-2">
                      <span>Poor</span>
                      <span>Excellent</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Barbell className="w-6 h-6 text-[#10B981]" weight="duotone" />
                  <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
                    Exercise Habits
                  </h2>
                </div>
                <div className="space-y-4">
                  <Select 
                    value={lifestyleData.exercise_frequency} 
                    onValueChange={(value) => handleLifestyleChange('exercise_frequency', value)}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-2" data-testid="exercise-select">
                      <SelectValue placeholder="How often do you exercise?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never</SelectItem>
                      <SelectItem value="rarely">Rarely (1-2 times/month)</SelectItem>
                      <SelectItem value="moderate">Moderate (1-2 times/week)</SelectItem>
                      <SelectItem value="frequent">Frequent (3-4 times/week)</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-4">
                <Link to="/dashboard" className="flex-1 md:flex-none">
                  <Button 
                    type="button"
                    variant="outline"
                    className="w-full h-14 rounded-xl border-2 border-gray-200 text-[#6B7280]"
                  >
                    Cancel
                  </Button>
                </Link>
                <Button 
                  onClick={handleNextStep}
                  className="flex-1 h-14 bg-[#5B21B6] hover:bg-[#4C1D95] rounded-xl text-lg font-semibold"
                  data-testid="next-step-btn"
                >
                  Next: Symptoms
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Symptoms */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              <div className="glass-card rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Fire className="w-6 h-6 text-[#FB923C]" weight="duotone" />
                  <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
                    Hot Flashes
                  </h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#1E1B4B] font-medium mb-4 block">
                      Hot flashes per day: {symptomsData.hot_flashes_frequency}
                    </Label>
                    <Slider
                      value={[symptomsData.hot_flashes_frequency]}
                      onValueChange={(value) => handleSymptomsChange('hot_flashes_frequency', value[0])}
                      min={0}
                      max={20}
                      step={1}
                      className="w-full"
                      data-testid="hot-flashes-slider"
                    />
                  </div>
                  <Select 
                    value={symptomsData.hot_flashes_severity} 
                    onValueChange={(value) => handleSymptomsChange('hot_flashes_severity', value)}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-2" data-testid="severity-select">
                      <SelectValue placeholder="Severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="mild">Mild</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="severe">Severe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="glass-card rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Drop className="w-6 h-6 text-[#EC4899]" weight="duotone" />
                  <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">
                    Menstrual Cycle
                  </h2>
                </div>
                <div className="space-y-4">
                  <Select 
                    value={symptomsData.cycle_regularity} 
                    onValueChange={(value) => handleSymptomsChange('cycle_regularity', value)}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-2" data-testid="cycle-regularity-select">
                      <SelectValue placeholder="Cycle regularity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular (21-35 days)</SelectItem>
                      <SelectItem value="irregular">Irregular</SelectItem>
                      <SelectItem value="absent">Absent/Stopped</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select 
                    value={symptomsData.cycle_length_change} 
                    onValueChange={(value) => handleSymptomsChange('cycle_length_change', value)}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-2" data-testid="cycle-change-select">
                      <SelectValue placeholder="Recent changes in cycle length" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="same">No change</SelectItem>
                      <SelectItem value="shorter">Getting shorter</SelectItem>
                      <SelectItem value="longer">Getting longer</SelectItem>
                      <SelectItem value="unpredictable">Unpredictable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="glass-card rounded-3xl p-6">
                <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] mb-6">
                  Other Symptoms
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <Label htmlFor="sleep_issues" className="text-[#1E1B4B]">
                      Sleep disturbances (waking at night)
                    </Label>
                    <Switch
                      id="sleep_issues"
                      checked={symptomsData.sleep_issues}
                      onCheckedChange={(checked) => handleSymptomsChange('sleep_issues', checked)}
                      data-testid="sleep-issues-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <Label htmlFor="mood_changes" className="text-[#1E1B4B]">
                      Mood changes or irritability
                    </Label>
                    <Switch
                      id="mood_changes"
                      checked={symptomsData.mood_changes}
                      onCheckedChange={(checked) => handleSymptomsChange('mood_changes', checked)}
                      data-testid="mood-changes-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <Label htmlFor="vaginal_dryness" className="text-[#1E1B4B]">
                      Vaginal dryness
                    </Label>
                    <Switch
                      id="vaginal_dryness"
                      checked={symptomsData.vaginal_dryness}
                      onCheckedChange={(checked) => handleSymptomsChange('vaginal_dryness', checked)}
                      data-testid="vaginal-dryness-switch"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 md:flex-none h-14 rounded-xl border-2 border-gray-200 text-[#6B7280]"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 h-14 bg-[#5B21B6] hover:bg-[#4C1D95] rounded-xl text-lg font-semibold"
                  data-testid="save-lifestyle-btn"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" weight="bold" />
                      Save All Data
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
